/**
 * SettingsBlog — Iter 191j · Blog Preferences.
 *
 * Squarespace-parity blog configuration (matches user-supplied reference):
 *   • Post URL format — token-driven slug pattern (%t %c %y %m %d) with live preview
 *   • Comments settings — enable/disable, moderation, sort order
 *   • Disqus shortname — swap native comments for Disqus
 *   • Simple Liking toggle — one-tap "like" without signup
 *
 * Extended for Entreprenatives (multi-blog + third-party ingest):
 *   • Blogs list — add / rename / remove multiple blog collections (each with
 *     its own URL slug); the storefront routes /blog/<slug>/…
 *   • External blog connections — Medium, Substack, Ghost, WordPress via RSS URL
 *     (we ingest posts read-only so the vendor doesn't have to rewrite content).
 *
 * Persistence:
 *   GET  /api/builder/settings                → returns { blog: {...} }
 *   PUT  /api/builder/settings { blog: {...} }
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Search, Loader2, Save, Plus, Trash2, Rss, MessageSquare, Heart, Link as LinkIcon, Info,
} from 'lucide-react';
import BackToSettings from './_BackToSettings';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const DEFAULTS = {
  post_url_format: '%t',
  comments: {
    enabled: true,
    require_approval: false,
    require_login: false,
    sort_order: 'newest',       // newest | oldest | most-liked
    close_after_days: 0,        // 0 = never
  },
  disqus_shortname: '',
  simple_liking: true,
  blogs: [
    { id: 'default', title: 'Blog', slug: 'blog', enabled: true },
  ],
  external_sources: [],         // [{ id, provider, title, feed_url, enabled }]
};

const PROVIDERS = [
  { id: 'medium',    label: 'Medium',    hint: 'Paste your Medium RSS URL (e.g. https://medium.com/feed/@you)' },
  { id: 'substack',  label: 'Substack',  hint: 'Your feed URL (e.g. https://yourname.substack.com/feed)' },
  { id: 'ghost',     label: 'Ghost',     hint: 'Ghost RSS URL (usually https://yourblog.com/rss/)' },
  { id: 'wordpress', label: 'WordPress', hint: 'WordPress feed URL (usually https://yourblog.com/feed/)' },
  { id: 'rss',       label: 'Other RSS', hint: 'Any RSS/Atom feed URL' },
];

const SORT_OPTIONS = [
  { id: 'newest',     label: 'Newest first' },
  { id: 'oldest',     label: 'Oldest first' },
  { id: 'most-liked', label: 'Most liked' },
];

/* ------------------------------ Primitives ------------------------------ */
function Toggle({ checked, onChange, disabled = false, testid }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      data-testid={testid}
      className={`relative w-10 h-6 rounded-full transition-colors ${checked ? 'bg-[#A78BFA]' : 'bg-white/15'} ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <span className={`absolute top-[2px] left-[2px] w-5 h-5 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-4' : 'translate-x-0'}`} />
    </button>
  );
}

function Field({ label, hint, children, testid }) {
  return (
    <div className="py-4 border-b border-white/[0.06] last:border-b-0" data-testid={testid}>
      <div className="flex items-start justify-between gap-6">
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-semibold text-white">{label}</div>
          {hint && <div className="mt-1 text-[12px] text-[#94A3B8] leading-snug">{hint}</div>}
        </div>
        <div className="shrink-0">{children}</div>
      </div>
    </div>
  );
}

function SectionCard({ title, description, children, testid }) {
  return (
    <section className="mt-8 rounded-xl bg-white/[0.03] border border-white/[0.08] p-6" data-testid={testid}>
      <h3 className="text-[15px] font-bold text-white tracking-tight">{title}</h3>
      {description && <p className="mt-1 text-[12.5px] text-[#94A3B8] leading-snug">{description}</p>}
      <div className="mt-4">{children}</div>
    </section>
  );
}

/* --------------------------- URL preview helper --------------------------- */
function urlPreview(format, siteBase) {
  const now = new Date();
  const parts = String(format || '%t')
    .replace(/%y/g, String(now.getFullYear()))
    .replace(/%m/g, String(now.getMonth() + 1).padStart(2, '0'))
    .replace(/%d/g, String(now.getDate()).padStart(2, '0'))
    .replace(/%c/g, 'category')
    .replace(/%t/g, 'my-first-post');
  const cleaned = parts.replace(/^\/+/, '').replace(/\/+/g, '/');
  return `${siteBase}/blog/${cleaned}`;
}

/* ---------------------------------- Page ---------------------------------- */
export default function SettingsBlog() {
  const [blog, setBlog] = useState(DEFAULTS);
  const [siteBase, setSiteBase] = useState('https://yourbrand.com');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const token = localStorage.getItem('token');

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/builder/settings`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const remote = res.data?.blog;
        if (remote && typeof remote === 'object') {
          setBlog({
            ...DEFAULTS,
            ...remote,
            comments: { ...DEFAULTS.comments, ...(remote.comments || {}) },
            blogs: Array.isArray(remote.blogs) && remote.blogs.length ? remote.blogs : DEFAULTS.blogs,
            external_sources: Array.isArray(remote.external_sources) ? remote.external_sources : [],
          });
        }
        // Best-effort read of the current subdomain to build a live URL preview
        try {
          const meta = await axios.get(`${API}/builder/site`, { headers: { Authorization: `Bearer ${token}` } });
          const sub = meta.data?.subdomain;
          if (sub) setSiteBase(`https://${sub}.entreprenatives.com`);
        } catch { /* silent */ }
      } catch { /* silent */ }
      finally { setLoading(false); }
    })();
  }, [token]);

  const update = (patch) => { setBlog((c) => ({ ...c, ...patch })); setDirty(true); };
  const updateComments = (patch) => { setBlog((c) => ({ ...c, comments: { ...c.comments, ...patch } })); setDirty(true); };

  const addBlog = () => update({
    blogs: [...(blog.blogs || []), { id: `blog_${Date.now()}`, title: 'New Blog', slug: `blog-${(blog.blogs || []).length}`, enabled: true }],
  });
  const updateBlog = (idx, patch) => {
    const next = [...blog.blogs];
    next[idx] = { ...next[idx], ...patch };
    update({ blogs: next });
  };
  const removeBlog = (idx) => {
    if (blog.blogs.length <= 1) { toast.error('At least one blog is required.'); return; }
    update({ blogs: blog.blogs.filter((_, i) => i !== idx) });
  };

  const addExternal = () => update({
    external_sources: [...(blog.external_sources || []), {
      id: `src_${Date.now()}`, provider: 'medium', title: '', feed_url: '', enabled: true,
    }],
  });
  const updateExternal = (idx, patch) => {
    const next = [...(blog.external_sources || [])];
    next[idx] = { ...next[idx], ...patch };
    update({ external_sources: next });
  };
  const removeExternal = (idx) => update({ external_sources: (blog.external_sources || []).filter((_, i) => i !== idx) });

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/builder/settings`, { blog }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Blog preferences saved');
      setDirty(false);
    } catch { toast.error('Could not save'); }
    finally { setSaving(false); }
  };

  const preview = useMemo(() => urlPreview(blog.post_url_format, siteBase), [blog.post_url_format, siteBase]);

  return (
    <div className="min-h-full" data-testid="settings-blog-page">
      {/* Top rail */}
      <div className="border-b border-white/[0.06] bg-white/[0.02] backdrop-blur-xl px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BackToSettings />
          <div className="w-px h-4 bg-white/10" />
          <h1 className="text-[13px] font-bold text-white uppercase tracking-[0.18em]">Settings</h1>
        </div>
        <div className="flex items-center gap-3">
          <button className="w-8 h-8 grid place-items-center text-white/60 hover:text-white hover:bg-white/[0.06] rounded-md transition-all" title="Search settings">
            <Search size={14} />
          </button>
          {saving && (
            <span className="text-[10px] uppercase tracking-[0.14em] text-[#A78BFA] font-bold animate-pulse">Saving…</span>
          )}
          <button
            type="button"
            onClick={save}
            disabled={!dirty || saving}
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-[11px] font-bold uppercase tracking-[0.14em] transition-all ${
              dirty && !saving ? 'bg-[#A78BFA] text-[#0E0E23] hover:bg-white' : 'bg-white/5 text-white/40 cursor-not-allowed'
            }`}
            data-testid="blog-save-btn"
          >
            <Save size={12} /> Save
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-8 py-10">
        <div className="text-[12px] text-[#64748B] mb-3">
          <span className="text-white/70 font-semibold">Website</span>
          <span className="mx-2">/</span>
          <span className="text-white">Blog Preferences</span>
        </div>

        <h2 className="text-[32px] font-bold text-white tracking-tight" style={{ fontFamily: 'Syne, Inter, sans-serif' }}>
          Blog Preferences
        </h2>
        <p className="mt-3 text-[14px] text-[#9CA3AF] leading-relaxed max-w-2xl">
          Configure how blog posts, comments, and reactions behave across every blog on your site.
        </p>

        {loading ? (
          <div className="mt-10 flex items-center gap-2 text-white/50">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        ) : (
          <>
            {/* POST URL FORMAT */}
            <SectionCard
              title="Post URL format"
              description="Create a standard URL structure for blog posts, products, and events."
              testid="section-post-url"
            >
              <input
                type="text"
                value={blog.post_url_format}
                onChange={(e) => update({ post_url_format: e.target.value })}
                className="w-full bg-[#0A0A1F] border border-white/10 rounded-md px-3 py-2 text-[13px] text-white font-mono focus:outline-none focus:border-[#A78BFA]/60"
                data-testid="post-url-format-input"
              />
              <p className="mt-3 text-[11.5px] text-[#94A3B8]">
                Example: <span className="text-white/80 font-mono">{preview}</span>
              </p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {[
                  { t: '%t', d: 'Post title slug' },
                  { t: '%c', d: 'Category' },
                  { t: '%y', d: 'Year (2026)' },
                  { t: '%m', d: 'Month (07)' },
                  { t: '%d', d: 'Day (16)' },
                ].map(({ t, d }) => (
                  <span
                    key={t}
                    title={d}
                    className="inline-flex items-center gap-1 text-[10.5px] font-mono px-2 py-1 rounded bg-white/[0.05] border border-white/10 text-white/80"
                  >
                    {t} <span className="text-white/40">·</span> <span className="text-white/60 font-sans">{d}</span>
                  </span>
                ))}
              </div>
            </SectionCard>

            {/* COMMENTS SETTINGS */}
            <SectionCard
              title="Comments settings"
              description="Options, sorting, and moderation."
              testid="section-comments"
            >
              <Field label="Enable comments" hint="Let readers reply to your posts.">
                <Toggle checked={blog.comments.enabled} onChange={(v) => updateComments({ enabled: v })} testid="comments-enabled-toggle" />
              </Field>
              <Field label="Require approval" hint="Comments stay hidden until you approve them.">
                <Toggle
                  checked={blog.comments.require_approval}
                  onChange={(v) => updateComments({ require_approval: v })}
                  disabled={!blog.comments.enabled}
                  testid="comments-approval-toggle"
                />
              </Field>
              <Field label="Require login" hint="Visitors must sign in to comment.">
                <Toggle
                  checked={blog.comments.require_login}
                  onChange={(v) => updateComments({ require_login: v })}
                  disabled={!blog.comments.enabled}
                  testid="comments-login-toggle"
                />
              </Field>
              <Field label="Sort order" hint="How comments are ordered by default.">
                <select
                  value={blog.comments.sort_order}
                  onChange={(e) => updateComments({ sort_order: e.target.value })}
                  disabled={!blog.comments.enabled}
                  className="bg-[#0A0A1F] border border-white/10 rounded-md px-3 py-2 text-[13px] text-white focus:outline-none focus:border-[#A78BFA]/60 min-w-[160px]"
                  data-testid="comments-sort-select"
                >
                  {SORT_OPTIONS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
              </Field>
              <Field label="Auto-close after (days)" hint="0 = never close. Recommended: 30–90.">
                <input
                  type="number" min="0" max="365"
                  value={blog.comments.close_after_days}
                  onChange={(e) => updateComments({ close_after_days: Math.max(0, Number(e.target.value) || 0) })}
                  disabled={!blog.comments.enabled}
                  className="w-24 bg-[#0A0A1F] border border-white/10 rounded-md px-3 py-2 text-[13px] text-white focus:outline-none focus:border-[#A78BFA]/60"
                  data-testid="comments-close-days-input"
                />
              </Field>
            </SectionCard>

            {/* DISQUS SHORTNAME */}
            <SectionCard title="Disqus shortname" description="Swap our native comments for Disqus.">
              <input
                type="text"
                value={blog.disqus_shortname}
                onChange={(e) => update({ disqus_shortname: e.target.value.trim() })}
                placeholder="yourshortname"
                className="w-full max-w-md bg-[#0A0A1F] border border-white/10 rounded-md px-3 py-2 text-[13px] text-white focus:outline-none focus:border-[#A78BFA]/60"
                data-testid="disqus-shortname-input"
              />
              <p className="mt-2 text-[11.5px] text-[#94A3B8]">
                Entering your Disqus shortname will replace our commenting system with Disqus throughout your site.
              </p>
            </SectionCard>

            {/* SIMPLE LIKING */}
            <SectionCard title="Simple liking" description="A nice way for any reader to show appreciation for posts by liking them.">
              <Field label="Use Simple Liking" hint="Allow users to like posts without logging in.">
                <Toggle checked={blog.simple_liking} onChange={(v) => update({ simple_liking: v })} testid="simple-liking-toggle" />
              </Field>
            </SectionCard>

            {/* BLOGS LIST */}
            <SectionCard
              title="Blogs"
              description="Run more than one blog — e.g. a Journal and a Press Room — each with its own URL path."
              testid="section-blogs"
            >
              <div className="flex justify-end mb-3">
                <button
                  type="button"
                  onClick={addBlog}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-[11px] font-bold uppercase tracking-wider bg-[#A78BFA] text-[#0E0E23] hover:bg-white transition-colors"
                  data-testid="add-blog-btn"
                >
                  <Plus size={12} /> Add blog
                </button>
              </div>
              <div className="space-y-2">
                {(blog.blogs || []).map((b, idx) => (
                  <div
                    key={b.id || idx}
                    className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto_auto] gap-2 items-center bg-white/[0.02] rounded-lg p-2 border border-white/[0.06]"
                    data-testid={`blog-row-${idx}`}
                  >
                    <input
                      type="text"
                      value={b.title}
                      onChange={(e) => updateBlog(idx, { title: e.target.value })}
                      placeholder="Blog title"
                      className="bg-[#0A0A1F] border border-white/10 rounded-md px-3 py-2 text-[13px] text-white focus:outline-none focus:border-[#A78BFA]/60"
                      data-testid={`blog-title-${idx}`}
                    />
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-[11px] text-[#64748B] font-mono">/blog/</span>
                      <input
                        type="text"
                        value={b.slug}
                        onChange={(e) => updateBlog(idx, { slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })}
                        placeholder="slug"
                        className="flex-1 bg-[#0A0A1F] border border-white/10 rounded-md px-3 py-2 text-[13px] text-white font-mono focus:outline-none focus:border-[#A78BFA]/60"
                        data-testid={`blog-slug-${idx}`}
                      />
                    </div>
                    <Toggle checked={b.enabled !== false} onChange={(v) => updateBlog(idx, { enabled: v })} testid={`blog-enabled-${idx}`} />
                    <button
                      type="button"
                      onClick={() => removeBlog(idx)}
                      className="w-9 h-9 grid place-items-center rounded-lg text-white/50 hover:text-white hover:bg-white/[0.06]"
                      title="Remove"
                      data-testid={`blog-remove-${idx}`}
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                ))}
              </div>
            </SectionCard>

            {/* EXTERNAL BLOG SOURCES */}
            <SectionCard
              title="Connect an external blog"
              description="Pull posts from another platform into your site. We ingest them read-only via RSS so you can keep publishing wherever you like."
              testid="section-external-sources"
            >
              <div className="flex justify-end mb-3">
                <button
                  type="button"
                  onClick={addExternal}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-[11px] font-bold uppercase tracking-wider bg-[#A78BFA] text-[#0E0E23] hover:bg-white transition-colors"
                  data-testid="add-external-btn"
                >
                  <Plus size={12} /> Connect
                </button>
              </div>

              {(blog.external_sources || []).length === 0 && (
                <div className="rounded-lg border border-dashed border-white/10 p-6 text-center">
                  <Rss size={20} className="mx-auto text-[#A78BFA]" />
                  <p className="mt-2 text-[12.5px] text-[#94A3B8]">
                    Not yet connected. We support <span className="text-white/80">Medium, Substack, Ghost, WordPress</span>, and any RSS or Atom feed.
                  </p>
                </div>
              )}

              <div className="space-y-3">
                {(blog.external_sources || []).map((s, idx) => (
                  <div key={s.id || idx} className="rounded-lg bg-white/[0.02] border border-white/[0.06] p-3" data-testid={`external-row-${idx}`}>
                    <div className="grid grid-cols-[140px_1fr_auto_auto] gap-2 items-center">
                      <select
                        value={s.provider}
                        onChange={(e) => updateExternal(idx, { provider: e.target.value })}
                        className="bg-[#0A0A1F] border border-white/10 rounded-md px-3 py-2 text-[13px] text-white focus:outline-none focus:border-[#A78BFA]/60"
                        data-testid={`external-provider-${idx}`}
                      >
                        {PROVIDERS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
                      </select>
                      <input
                        type="text"
                        value={s.title}
                        onChange={(e) => updateExternal(idx, { title: e.target.value })}
                        placeholder="Display name (e.g. 'Notes on the Craft')"
                        className="bg-[#0A0A1F] border border-white/10 rounded-md px-3 py-2 text-[13px] text-white focus:outline-none focus:border-[#A78BFA]/60"
                        data-testid={`external-title-${idx}`}
                      />
                      <Toggle checked={s.enabled !== false} onChange={(v) => updateExternal(idx, { enabled: v })} testid={`external-enabled-${idx}`} />
                      <button
                        type="button"
                        onClick={() => removeExternal(idx)}
                        className="w-9 h-9 grid place-items-center rounded-lg text-white/50 hover:text-white hover:bg-white/[0.06]"
                        title="Remove"
                        data-testid={`external-remove-${idx}`}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <LinkIcon size={12} className="text-[#A78BFA] shrink-0" />
                      <input
                        type="text"
                        value={s.feed_url}
                        onChange={(e) => updateExternal(idx, { feed_url: e.target.value.trim() })}
                        placeholder={PROVIDERS.find((p) => p.id === s.provider)?.hint || 'Feed URL'}
                        className="flex-1 bg-[#0A0A1F] border border-white/10 rounded-md px-3 py-2 text-[12px] text-white font-mono focus:outline-none focus:border-[#A78BFA]/60"
                        data-testid={`external-feed-${idx}`}
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4 flex items-start gap-2 rounded-lg bg-[#A78BFA]/8 border border-[#A78BFA]/20 p-3">
                <Info size={13} className="text-[#A78BFA] mt-0.5 shrink-0" />
                <p className="text-[11.5px] text-white/75 leading-snug">
                  We refresh external feeds every 30 minutes. Comments, likes, and edits still happen on the source platform —
                  we just mirror the posts on your storefront so visitors have a single reading experience.
                </p>
              </div>
            </SectionCard>

            {/* Legend footer */}
            <div className="mt-8 flex items-center gap-4 text-[11px] text-white/40">
              <span className="inline-flex items-center gap-1.5"><MessageSquare size={11} /> Comments</span>
              <span className="inline-flex items-center gap-1.5"><Heart size={11} /> Reactions</span>
              <span className="inline-flex items-center gap-1.5"><Rss size={11} /> RSS</span>
            </div>

            <div className="h-16" />
          </>
        )}
      </div>
    </div>
  );
}
